<template>
    <div id="treatment-board-v2">
        <div
            class="table-container mb-2"
            ref="tableContainer"
            :style="_isTvView ? 'height: calc(100vh - 100px);' : ''"
        >
            <table
                class="table table-light table-bordered no-header-border fixed-table"
            >
                <colgroup>
                    <!-- Define fixed column widths -->
                    <col span="2" class="fixed-col-180" />
                    <col class="fixed-col-110" />
                    <template v-if="treatmentBoardView == 'timeline'">
                        <col
                            v-for="datetime in datetimes"
                            :key="String(datetime.oriDate)"
                            class="fixed-col-70"
                        />
                    </template>
                    <template v-else-if="treatmentBoardView == 'activity'">
                        <col />
                    </template>
                </colgroup>
                <thead>
                    <tr
                        v-if="treatmentBoardView != 'activity'"
                        class="fixed-row"
                    >
                        <th
                            scope="col"
                            colspan="2"
                            class="border-dark fixed-col-180 border-break"
                        ></th>
                        <th
                            scope="col"
                            class="border-secondary fixed-col-110"
                        ></th>
                        <template v-if="treatmentBoardView == 'timeline'">
                            <th
                                v-for="datetime in datetimes"
                                :key="String(datetime.oriDate)"
                                class="border-secondary text-center"
                                :style="{
                                    color: datetime.isCurrentTime ? 'red' : null
                                }"
                            >
                                <div class="timeline-header">
                                    <span>{{
                                        datetime.dateSimplify.toUpperCase()
                                    }}</span>
                                    <span
                                        >{{ datetime.hour }}
                                        {{ datetime.meridiem }}</span
                                    >
                                </div>
                            </th>
                        </template>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="filteredTreatments.length == 0">
                        <td :colspan="datetimes.length">
                            <i>No records available</i>
                        </td>
                    </tr>
                    <tr
                        v-for="treatment in filteredTreatments"
                        :key="treatment.id"
                        class="fixed-row"
                    >
                        <th
                            scope="row"
                            colspan="2"
                            class="border-dark p-0 fixed-col-180 align-top"
                        >
                            <TreatmentGeneralDetails
                                :treatment="treatment"
                                :triageOptions="triageOptions"
                                :dialogs="dialogs"
                                v-on="$listeners"
                            ></TreatmentGeneralDetails>
                        </th>
                        <th
                            scope="row"
                            class="border-secondary fixed-col-110 align-top"
                        >
                            <TreatmentSummaryDetails
                                ref="TreatmentSummaryDetails"
                                :treatment="treatment"
                                :activities="treatment.segregatedActivities"
                                :status="TreatmentStatus(treatment)"
                                :cellId="`${treatment.id}`"
                                :prepareDateTime="prepareDateTime"
                                :dialogs="dialogs"
                                :parentRef="$refs.tableContainer"
                                :labelOptions="labelOptions"
                            ></TreatmentSummaryDetails>
                        </th>
                        <template
                            v-if="
                                treatmentBoardView == 'timeline' &&
                                datetimes.length > 0
                            "
                        >
                            <td
                                v-for="(datetime, hour) in datetimes"
                                :key="hour"
                                scope="rowgroup"
                                class="activity-card-cell border-secondary position-relative"
                            >
                                <div
                                    v-if="datetime.isCurrentTime"
                                    :style="minuteLineStyle"
                                    v-b-tooltip.hover.left="currentLineTooltip"
                                >
                                    <!-- This div represents the line -->
                                </div>
                                <TreatmentTimelinedDetails
                                    :id="`${treatment.id}_${hour}`"
                                    :ref="`${treatment.id}_${hour}`"
                                    :treatment="treatment"
                                    :activities="
                                        isActivitiesBelongsHere(treatment, hour)
                                    "
                                    :cellId="`${treatment.id}_${hour}`"
                                    :cellDate="datetime"
                                    :dialogs="dialogs"
                                    :prepareDateTime="prepareDateTime"
                                    :parentRef="$refs.tableContainer"
                                    :labelOptions="labelOptions"
                                ></TreatmentTimelinedDetails>
                            </td>
                        </template>
                        <template v-else-if="treatmentBoardView == 'activity'">
                            <td
                                scope="rowgroup"
                                class="activity-card-cell border-secondary"
                            >
                                <div class="flex-scroll">
                                    <TreatmentActivityDetails
                                        v-for="(activity,
                                        index) in treatment.activities"
                                        :key="index"
                                        :activity="activity"
                                        :treatment="treatment"
                                        :prepareDateTime="prepareDateTime"
                                        :dialogs="dialogs"
                                        :labelOptions="labelOptions"
                                        class="fixed-col-150"
                                    ></TreatmentActivityDetails>
                                </div>
                            </td>
                        </template>
                    </tr>
                </tbody>
            </table>
        </div>
        <span
            v-if="treatmentBoardView == 'timeline'"
            class="tip-text text-muted"
            >Tip: You can also use 'Shift' + mouse wheel or arrow keys to
            navigate the timeline.
        </span>
    </div>
</template>
<script>
import TriageService from "@/services/triage";
import TreatmentTimelinedDetails from "./treatment-timeline-details";
import TreatmentActivityDetails from "./treatment-activity-details";
import TreatmentGeneralDetails from "./treatment-general-details";
import TreatmentSummaryDetails from "./treatment-summary-details";
import ScheduleHelper from "../helper";
import TreatmentTimelineMixin from "@/mixins/treatment-timeline-details";
import TreatmentActivityMixin from "@/mixins/treatment-activity-details";
import { cloneDeep } from "lodash";

export default {
    name: "TreatmentRowNew",
    mixins: [ScheduleHelper, TreatmentActivityMixin, TreatmentTimelineMixin],
    components: {
        TreatmentTimelinedDetails,
        TreatmentActivityDetails,
        TreatmentGeneralDetails,
        TreatmentSummaryDetails
    },
    props: {
        treatments: {
            type: Array,
            default() {
                return [];
            }
        },
        treatment: {
            type: Object,
            default() {
                return {};
            }
        },
        activities: {
            type: Object,
            default() {
                return {};
            }
        },
        treatmentBoardView: {
            type: String,
            default: "timeline"
        },
        dialogs: {
            type: Object,
            default() {
                return {};
            }
        },
        filter: {
            type: Object
        }
    },
    data() {
        return {
            currentQuery: "",
            triageOptions: [],
            computeFilteredTreatments: 0
        };
    },
    mounted() {
        this.setToCurrentTime(); // mixin @treatment-timeline-details.js
        this.getTriageList();

        // Add global listener for keydown events
        window.addEventListener("keydown", this.handleKeydown);
        window.addEventListener("wheel", this.handleKeydown);
    },
    beforeDestroy() {
        // Clean up event listener
        window.removeEventListener("keydown", this.handleKeydown);
        window.removeEventListener("wheel", this.handleKeydown);
    },
    computed: {
        filteredTreatments() {
            this.computeFilteredTreatments;
            const query = this.currentQuery.toLowerCase();
            // If you're in a modern browser or Node.js environment:
            let treatments = this.filterTreatments(this.treatments);

            treatments = treatments
                .map(treatment => this.getDueForActivity(treatment))
                .filter(treatment => !treatment.isDischarged);

            if (!query) return treatments;

            return treatments.filter(item =>
                (item.patient?.name || "").toLowerCase().includes(query)
            );
        },
        labelOptions() {
            return this.$store.state.reference["treatment-task-label"];
        }
    },
    methods: {
        filterTreatments(oriTreatments) {
            let treatments = cloneDeep(oriTreatments);
            let filteredTreatments = treatments.filter(treatment => {
                // Check active_triage status if triage_status filter is provided
                let triageMatches = true;
                if (
                    this.filter.triage_status &&
                    this.filter.triage_status.length > 0
                ) {
                    let status = treatment.active_triage?.triage_type_id || -1;
                    triageMatches = this.filter.triage_status.includes(
                        String(status)
                    );
                }

                // Check assigned staff if assign_to filter is provided
                let staffMatches = true;
                if (this.filter.assign_to && this.filter.assign_to.length > 0) {
                    if (treatment.staffs.length > 0) {
                        staffMatches = treatment.staffs.some(staff =>
                            this.filter.assign_to.includes(staff.id)
                        );
                    } else {
                        let emptyStaff = -1;
                        staffMatches = this.filter.assign_to.includes(
                            String(emptyStaff)
                        );
                    }
                }

                // Check treatment status if treatment_status filter is provided
                let statusMatches = true;
                treatment.isDischarged = false;
                if (
                    this.filter.treatment_status &&
                    this.filter.treatment_status.length > 0
                ) {
                    statusMatches = treatment.treatment_status.some(status =>
                        this.filter.treatment_status.includes(
                            String(status.status_id)
                        )
                    );
                } else if (
                    treatment.treatment_status[0].status_id ==
                    this.TREATMENT_STATUS.DISCHARGED
                ) {
                    treatment.isDischarged = true;
                }

                // Check overnight stay if the filter is provided
                let overnightStayMatches = true;
                if (this.filter.overnight_stay) {
                    overnightStayMatches = treatment.overnight_stay;
                }

                // Check overdue if the filter is provided
                let dueMatches = true;
                if (
                    this.filter.activity_due &&
                    this.filter.activity_due.length > 0
                ) {
                    let preparedActivities = this.prepareActivities(
                        treatment,
                        true
                    );
                    let activeActivities = preparedActivities.active;
                    let overdueActivities = preparedActivities.overdue;

                    // Check if any activity is due (usually 5 minutes below)
                    let isOverdueAvailable = overdueActivities.length > 0;

                    // Check if any activity is due within 60 minutes
                    let isDueInSixtyMinutes = activeActivities.some(
                        activity => activity.dueCalculated.diff <= 60
                    );

                    // Check filters
                    let checkForOverdueAvailable = this.filter.activity_due.includes(
                        "1"
                    );
                    let checkForDueInSixtyMinute = this.filter.activity_due.includes(
                        "2"
                    );

                    dueMatches =
                        checkForOverdueAvailable && checkForDueInSixtyMinute
                            ? isOverdueAvailable || isDueInSixtyMinutes
                            : checkForOverdueAvailable
                            ? isOverdueAvailable
                            : checkForDueInSixtyMinute
                            ? isDueInSixtyMinutes
                            : true;
                }

                return (
                    triageMatches &&
                    staffMatches &&
                    statusMatches &&
                    overnightStayMatches &&
                    dueMatches
                );
            });

            this.$emit("get-filter-options", filteredTreatments);
            return filteredTreatments;
        },
        async getTriageList() {
            await TriageService.getTriageTypeLists()
                .then(res => {
                    if (res && res.code === 200) {
                        this.triageOptions = res.resources;
                    } else {
                        throw new Error(
                            "Error: Unable to get the triage list."
                        );
                    }
                })
                .catch(e => {
                    this._message(
                        "Error: Unable to get the triage list.",
                        "danger"
                    );
                });
        },
        handleKeydown(event) {
            if (this.treatmentBoardView == "timeline") {
                if (event.shiftKey) {
                    if (event.key === "ArrowLeft") {
                        this.onNavigateButtonClick(false); // mixin @treatment-timeline-details.js
                    } else if (event.key === "ArrowRight") {
                        this.onNavigateButtonClick(); // mixin @treatment-timeline-details.js
                    }
                    if (event.wheelDeltaX > 0) {
                        this.onNavigateButtonClick(false); // mixin @treatment-timeline-details.js
                    } else if (event.wheelDeltaX < 0) {
                        this.onNavigateButtonClick(); // mixin @treatment-timeline-details.js
                    }
                }
            }
        },
        searchPatients(query = "") {
            this.currentQuery = query;
        },
        isActivitiesBelongsHere(treatment, hour) {
            const cellDate = this.datetimes[hour];
            // Convert the Unix timestamp to a DateTime object (assumed UTC)
            const dateFromCellDate = cellDate.oriDate;
            const preparedActivities = treatment.segregatedActivities;

            let filteredCompletedActivities = [];
            let filteredOverdueActivities = [];
            let filteredActiveActivities = [];
            Object.keys(preparedActivities).forEach(typeActivity => {
                preparedActivities[typeActivity].forEach(activity => {
                    // Convert the Unix timestamp to a DateTime object
                    let createAtDateSetZone = this._toDateTime(
                        activity.referTime
                    );

                    // Compare just the calendar date components
                    if (createAtDateSetZone) {
                        const sameDate =
                            createAtDateSetZone.year ===
                                dateFromCellDate.year &&
                            createAtDateSetZone.month ===
                                dateFromCellDate.month &&
                            createAtDateSetZone.day === dateFromCellDate.day &&
                            createAtDateSetZone.hour === dateFromCellDate.hour;
                        if (sameDate) {
                            if (typeActivity == "completed") {
                                filteredCompletedActivities.push(activity);
                            } else if (typeActivity == "overdue") {
                                filteredOverdueActivities.push(activity);
                            } else if (typeActivity == "active") {
                                filteredActiveActivities.push(activity);
                            }
                        }
                    }
                });
            });
            return {
                filteredCompletedActivities: filteredCompletedActivities,
                filteredOverdueActivities: filteredOverdueActivities,
                filteredActiveActivities: filteredActiveActivities
            };
        }
    }
};
</script>

<style scoped>
.activity-card-cell {
    padding: 0 !important; /* p-0 */
    height: 1px;
}
.timeline-header {
    display: flex; /* d-flex */
    flex-direction: column; /* flex-column */
    justify-content: center; /* justify-content-center */
    height: 100%; /* h-100 */
}
.flex-scroll {
    display: flex; /* d-flex */
    height: 100%; /* full height */
    overflow-x: scroll; /* allows horizontal scrolling */
}
@supports (-moz-appearance: none) {
    .flex-scroll {
        min-height: 200px;
    }
}
.tip-text {
    font-weight: 300; /* font-weight-light */
    vertical-align: baseline; /* align-baseline */
    padding-left: 0.5rem; /* pl-2 */
    font-size: smaller; /* smaller font size */
}
.border-break {
    white-space: break-spaces; /* Allows breaking of long text */
}
.table-container {
    /* Dynamically adjust the height relative to the viewport */
    height: calc(100vh - 280px); /* adjust offset as needed */
    min-height: 400px; /* enforces min-height requirement */
    overflow-y: auto; /* allows vertical scroll */
}
/* Make the header row sticky */
thead > tr {
    position: sticky;
    top: 0;
    z-index: 4; /* higher z-index so it stays above table body rows */
    background-color: #fbfbfb; /* match your table header color */
}

/* If using table-bordered and want to retain other borders */
.no-header-border.table-bordered thead th {
    border-top: none; /* Removes the top border */
    border-left: none; /* Removes the left border */
    border-right: none; /* Removes the right border */
}
.fixed-table {
    table-layout: fixed;
    width: 100%;
}

/* Define fixed column widths */
.fixed-col-180 {
    width: 180px;
    min-width: 180px;
}

.fixed-col-150 {
    width: 150px;
    min-width: 150px;
}

.fixed-col-110 {
    width: 110px;
    min-width: 110px;
}

.fixed-col-70 {
    width: 70px;
    min-width: 70px;
}

/* Set fixed row heights */
.fixed-row {
    height: 70px; /* Adjust as needed */
    overflow: hidden;
}

/* Ensure cell content fits within fixed height */
.fixed-row td,
.fixed-row th {
    padding: 10px; /* Adjust padding to fit content */
    vertical-align: middle;
}

.table {
    border-collapse: separate;
    border-spacing: 0;
}

.table > thead > tr > th:first-child,
.table > tbody > tr > th:first-child {
    position: sticky;
    background-color: #fbfbfb;
    left: 0;
    z-index: 3;
}

.table > thead > tr > th:nth-child(2),
.table > tbody > tr > th:nth-child(2) {
    position: sticky;
    background-color: #fbfbfb;
    left: 360px;
    z-index: 2;
}
</style>
